import clsx from 'clsx';
import { useTranslation } from '@client-app/i18n';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { useEffect, useState, type KeyboardEvent } from 'react';
import Modal from '../Modal';
import IntegrationConnectGuide, { getIntegrationConnectGuideModalWidth } from './IntegrationConnectGuide';
import {
    canSubmitIntegrationCredentialForm,
    type IntegrationCredentialTool,
    type IntegrationCredentialValues,
} from './integrationCredentialForm';

interface IntegrationCredentialsModalProps {
    isOpen: boolean;
    onClose: () => void;
    tool: IntegrationCredentialTool | null;
    values: IntegrationCredentialValues;
    onValuesChange: (values: IntegrationCredentialValues) => void;
    onConfirm: () => void;
    isLoading?: boolean;
    /** @deprecated Business-group assignment occurs after connection succeeds. */
    brandOptions?: unknown[];
    selectedBrandIds?: string[];
    onSelectedBrandIdsChange?: (brandIds: string[]) => void;
}

type SecretField = 'apiKey' | 'consumerKey' | 'consumerSecret' | 'accessToken';

export default function IntegrationCredentialsModal({
    isOpen,
    onClose,
    tool,
    values,
    onValuesChange,
    onConfirm,
    isLoading = false,
}: IntegrationCredentialsModalProps) {
    const { t } = useTranslation();
    const [visibleSecretFields, setVisibleSecretFields] = useState<Set<SecretField>>(() => new Set());

    useEffect(() => {
        if (!isOpen) {
            setVisibleSecretFields(new Set());
        }
    }, [isOpen]);

    if (!isOpen || !tool) {
        return null;
    }

    const canConfirm = canSubmitIntegrationCredentialForm(tool, values);
    const modalWidth = getIntegrationConnectGuideModalWidth(tool, 'max-w-lg');

    const updateValue = (key: keyof IntegrationCredentialValues, value: string) => {
        onValuesChange({ ...values, [key]: value });
    };

    const handleClose = () => {
        if (!isLoading) {
            onClose();
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && canConfirm && !isLoading) {
            onConfirm();
        }
    };

    const toggleSecretField = (field: SecretField) => {
        setVisibleSecretFields((current) => {
            const next = new Set(current);
            if (next.has(field)) {
                next.delete(field);
            } else {
                next.add(field);
            }
            return next;
        });
    };

    const renderTextField = ({
        id,
        label,
        value,
        placeholder,
        onChange,
        autoComplete = 'off',
        autoFocus = false,
    }: {
        id: string;
        label: string;
        value: string;
        placeholder: string;
        onChange: (value: string) => void;
        autoComplete?: string;
        autoFocus?: boolean;
    }) => (
        <div>
            <label htmlFor={id} className="mb-2 block text-sm font-semibold text-[#1A162F]">
                {label} <span className="text-red-500">*</span>
            </label>
            <input
                id={id}
                name={id}
                type="text"
                autoComplete={autoComplete}
                spellCheck={false}
                autoFocus={autoFocus}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-[#1A162F] outline-none transition-all placeholder:text-[#666F8D] focus:border-[#1A162F] focus:ring-2 focus:ring-[#1A162F]/20"
            />
        </div>
    );

    const renderSecretField = ({
        id,
        field,
        label,
        value,
        placeholder,
        autoFocus = false,
    }: {
        id: string;
        field: SecretField;
        label: string;
        value: string;
        placeholder: string;
        autoFocus?: boolean;
    }) => {
        const isVisible = visibleSecretFields.has(field);
        const visibilityLabel = isVisible
            ? t('website.integrationsSettings.hideCredential', { credential: label })
            : t('website.integrationsSettings.showCredential', { credential: label });

        return (
            <div>
                <label htmlFor={id} className="mb-2 block text-sm font-semibold text-[#1A162F]">
                    {label} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                    <input
                        id={id}
                        name={id}
                        type={isVisible ? 'text' : 'password'}
                        autoComplete="new-password"
                        spellCheck={false}
                        autoFocus={autoFocus}
                        value={value}
                        onChange={(event) => updateValue(field, event.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 pr-11 text-sm text-[#1A162F] outline-none transition-all placeholder:text-[#666F8D] focus:border-[#1A162F] focus:ring-2 focus:ring-[#1A162F]/20"
                    />
                    <button
                        type="button"
                        aria-label={visibilityLabel}
                        title={visibilityLabel}
                        onClick={() => toggleSecretField(field)}
                        className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-[#666F8D] transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F] focus:outline-none focus:ring-2 focus:ring-[#1A162F]/20"
                    >
                        {isVisible ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t('website.integrationsSettings.connectToolTitle', { tool })}
            width={modalWidth}
            bodyClassName="px-6 pb-6 pt-1"
            titleClassName="text-[#1A162F]"
        >
            <div className="space-y-4">
                <IntegrationConnectGuide toolName={tool} />

                {tool === 'Semrush'
                    ? renderSecretField({
                          id: 'semrush-api-key',
                          field: 'apiKey',
                          label: t('website.integrationsSettings.apiKey'),
                          value: values.apiKey,
                          placeholder: t('website.integrationsSettings.semrushApiKeyPlaceholder'),
                          autoFocus: true,
                      })
                    : null}

                {tool === 'WooCommerce' ? (
                    <>
                        {renderTextField({
                            id: 'woocommerce-store-url',
                            label: t('website.integrationsSettings.storeUrl'),
                            value: values.storeUrl,
                            placeholder: t('website.integrationsSettings.woocommerceStoreUrlPlaceholder'),
                            onChange: (value) => updateValue('storeUrl', value),
                            autoComplete: 'url',
                            autoFocus: true,
                        })}
                        {renderSecretField({
                            id: 'woocommerce-consumer-key',
                            field: 'consumerKey',
                            label: t('website.integrationsSettings.woocommerceConsumerKey'),
                            value: values.consumerKey,
                            placeholder: t('website.integrationsSettings.woocommerceConsumerKeyPlaceholder'),
                        })}
                        {renderSecretField({
                            id: 'woocommerce-consumer-secret',
                            field: 'consumerSecret',
                            label: t('website.integrationsSettings.woocommerceConsumerSecret'),
                            value: values.consumerSecret,
                            placeholder: t('website.integrationsSettings.woocommerceConsumerSecretPlaceholder'),
                        })}
                    </>
                ) : null}

                {tool === 'Magento' ? (
                    <>
                        {renderTextField({
                            id: 'magento-store-domain',
                            label: t('website.integrationsSettings.magentoStoreDomain'),
                            value: values.storeDomain,
                            placeholder: t('website.integrationsSettings.magentoStoreDomainPlaceholder'),
                            onChange: (value) => updateValue('storeDomain', value),
                            autoComplete: 'url',
                            autoFocus: true,
                        })}
                        {renderTextField({
                            id: 'magento-store-code',
                            label: t('website.integrationsSettings.magentoStoreCode'),
                            value: values.storeCode,
                            placeholder: t('website.integrationsSettings.magentoStoreCodePlaceholder'),
                            onChange: (value) => updateValue('storeCode', value),
                        })}
                        {renderSecretField({
                            id: 'magento-access-token',
                            field: 'accessToken',
                            label: t('website.integrationsSettings.magentoAccessToken'),
                            value: values.accessToken,
                            placeholder: t('website.integrationsSettings.magentoAccessTokenPlaceholder'),
                        })}
                    </>
                ) : null}

                <div className="flex justify-end gap-3 pt-2">
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={isLoading}
                        className={clsx(
                            'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                            isLoading
                                ? 'cursor-not-allowed text-[#9AA2B8]'
                                : 'text-[#666F8D] hover:bg-gray-100 hover:text-[#1A162F]',
                        )}
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={!canConfirm || isLoading}
                        className={clsx(
                            'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors',
                            canConfirm && !isLoading
                                ? 'bg-[#1A162F] text-white hover:bg-[#1A162F]/90'
                                : isLoading
                                  ? 'cursor-wait bg-gray-300 text-[#666F8D]'
                                  : 'cursor-not-allowed bg-gray-300 text-[#666F8D]',
                        )}
                    >
                        {isLoading ? (
                            <span
                                aria-hidden="true"
                                className="h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent"
                            />
                        ) : null}
                        {isLoading
                            ? t('website.integrationsSettings.connectingTool', { tool })
                            : t('website.integrationsSettings.connectToolAction', { tool })}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
